import {useRef, useState} from 'react';
import {Archive, RotateCcw} from 'lucide-react';
import {Button} from '@/components/ui/button';

export interface ArchiveOutcome {complete:boolean; archived:boolean; results:Array<{environment:string;status:string;message?:string}>;}
const environmentLabel:Record<string,string>={danstaging:'Dan staging',staging:'Shared staging',production:'Production'};
/** The server validates scope and provenance; this control never submits guessed source IDs. */
export function ArchiveWinningAdAction({winningAdId,archived,canvasSource,onComplete}:{winningAdId:number;archived:boolean;canvasSource?:{clientId:string;projectId:number};onComplete?:(archived:boolean)=>void}) {
 const [confirm,setConfirm]=useState(false),[busy,setBusy]=useState(false),[error,setError]=useState('');
 const [outcome,setOutcome]=useState<ArchiveOutcome|null>(null);
 const operation=useRef<{id:string;action:'archive'|'restore'}|null>(null);
 const submitting=useRef(false);
 const action=operation.current?.action??(archived?'restore':'archive');
 const label=action==='archive'?'Archive':'Restore';
 async function submit(){
  if(submitting.current)return;
  submitting.current=true;setBusy(true);setError('');setConfirm(false);
  operation.current??={id:crypto.randomUUID(),action};
  try{
   const res=await fetch(`/api/admin/winning-ads/${winningAdId}/${operation.current.action}`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({operationId:operation.current.id,canvasSource})});
   const result=await res.json();
   if(!res.ok)throw new Error(result.error||`${label} failed. Please retry.`);
   setOutcome(result);
   if(result.complete){operation.current=null;onComplete?.(result.archived);}
  }catch(e){setError(e instanceof Error?e.message:'Request failed. Retry to check and finish this operation.');}
  finally{submitting.current=false;setBusy(false);}
 }
 return <div className="space-y-2">
  {!outcome?.complete&&<Button size="sm" variant="outline" disabled={busy} onClick={()=>operation.current?void submit():setConfirm(true)}>
   {action==='archive'?<Archive size={14} className="mr-1"/>:<RotateCcw size={14} className="mr-1"/>}
   {busy?'Syncing…':operation.current?`Retry ${action}`:`${label} source`}
  </Button>}
  {confirm&&<div role="alertdialog" aria-label={`${label} Winning Ad`} className="rounded border border-amber-300 bg-amber-50 p-3 text-sm space-y-2">
   <p>{action==='archive'?'Archive this Winning Ad across Dan staging, shared staging, and production? It will no longer be selected for new ads. Its image and metadata will be kept, and you can restore it from Archived.':'Restore this Winning Ad across Dan staging, shared staging, and production? It will be available again wherever it meets the library eligibility rules.'}</p>
   <div className="flex gap-2"><Button size="sm" onClick={()=>void submit()}>Confirm {action}</Button><Button size="sm" variant="ghost" onClick={()=>setConfirm(false)}>Cancel</Button></div>
  </div>}
  {error&&<p role="alert" className="text-sm text-red-700">{error}</p>}
  {outcome&&<div role="status" className="text-xs space-y-1">
   <p>{outcome.complete?(outcome.archived?'Archived across all three environments.':'Restored across all three environments.'):`${label} incomplete — retry to finish syncing.`}</p>
   {outcome.results.map(r=><p key={r.environment}>{environmentLabel[r.environment]??r.environment}: {r.status==='applied'?'Synced':r.status==='absent'?'No verified match — archive state synced':r.message||'Not synced'}</p>)}
  </div>}
 </div>;
}
